import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { User, ShoppingBag, Eye, EyeOff, ChevronRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // After auto-login bounce back to the screen the fan tried to pay from.
  const returnTo = (() => {
    try {
      const r = new URLSearchParams(location.search).get("returnTo");
      if (r && r.startsWith("/") && !r.startsWith("//")) return r;
    } catch { /* noop */ }
    return null;
  })();
  const [step, setStep] = useState(1); // 1=role choice, 2=form
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fanName, setFanName] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  const handleGoogleLogin = () => {
    if (returnTo) {
      try { sessionStorage.setItem("startip_post_login_redirect", returnTo); } catch { /* noop */ }
    }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const websites = [];
      await axios.post(`${API}/auth/register`,
        { email: form.email, password: form.password, name: form.name, role, websites,
          invite_code: (role === "creator" && inviteCode.trim()) ? inviteCode.trim() : null },
        { withCredentials: true }
      );
      toast.success(t("auth.accountCreatedLogin"));
      // Auto-login after register
      const login = await axios.post(`${API}/auth/login`,
        { email: form.email, password: form.password },
        { withCredentials: true }
      );

      // Si es creadora y escribió un nombre de fan, guardar el referido
      if (login.data.role !== "consumer" && fanName.trim()) {
        try {
          const refRes = await axios.post(`${API}/promotions/fan-referral`,
            { fan_name: fanName.trim() },
            { withCredentials: true }
          );
          toast.success(refRes.data.message, { duration: 6000 });
        } catch (refErr) {
          // Error no fatal: el registro ya se completó
          toast.warning(refErr.response?.data?.detail || t("auth.referralError"));
        }
        // Marcar que ya completó este paso para que /google-referral no vuelva a aparecer
        await axios.post(`${API}/me/inviter-step-done`, {}, { withCredentials: true }).catch(() => {});
      }

      navigate(returnTo || (login.data.role === "consumer" ? "/my-services" : "/dashboard"));
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Este nombre para mostrar ya está en uso. Elige otro diferente.");
      } else {
        toast.error(err.response?.data?.detail || t("auth.registerError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center relative">
          <h1 className="font-heading font-black text-4xl text-white uppercase tracking-tight">
            STAR<span className="text-neon-green">TIP</span>
          </h1>
          <p className="text-zinc-500 text-sm font-body mt-1">{t("auth.register")}</p>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="space-y-3">
              <p className="text-sm text-zinc-400 font-body text-center">{t("auth.chooseRole")}</p>

              {/* Consumer / Fan card — primera opción */}
              <button onClick={() => { setRole("consumer"); setStep(2); }}
                className="w-full p-5 rounded-2xl border-2 border-border bg-card hover:border-primary hover:bg-primary/5 transition-all text-left group"
                data-testid="role-consumer-btn">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                    <ShoppingBag className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-bold text-white text-base uppercase">{t("auth.fan")}</p>
                    <p className="text-xs text-zinc-500 font-body mt-0.5">{t("auth.fanDescription")}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-primary transition-colors" />
                </div>
              </button>

              {/* Creator card */}
              <button onClick={() => { setRole("creator"); setStep(2); }}
                className="w-full p-5 rounded-2xl border-2 border-border bg-card hover:border-neon-green hover:bg-neon-green/5 transition-all text-left group"
                data-testid="role-creator-btn">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-neon-green/10 border border-neon-green/30 flex items-center justify-center shrink-0">
                    <Star className="w-5 h-5 text-neon-green" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-bold text-white text-base uppercase">{t("auth.creator")}</p>
                    <p className="text-xs text-zinc-500 font-body mt-0.5">{t("auth.creatorDescription")}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-neon-green transition-colors" />
                </div>
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-zinc-600 font-body">{t("auth.orContinueWith")}</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Google button */}
              <Button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-white hover:bg-zinc-100 text-black font-body font-semibold rounded-xl"
                data-testid="register-google-btn"
              >
                <img src="https://www.google.com/favicon.ico" alt="G" className="w-4 h-4 mr-2" />
                {t("auth.googleLogin")}
              </Button>

              <p className="text-center text-xs text-zinc-600 font-body">
                {t("auth.alreadyHaveAccount")}{" "}
                <button onClick={() => navigate("/login")} className="text-primary hover:underline">{t("auth.login")}</button>
              </p>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setStep(1)} className="text-zinc-500 hover:text-white text-xs font-body">{t("auth.authBack")}</button>
                <span className="text-xs text-zinc-600 font-body">|</span>
                <div className="flex items-center gap-1.5">
                  {role === "creator"
                    ? <Star className="w-3.5 h-3.5 text-neon-green" />
                    : <ShoppingBag className="w-3.5 h-3.5 text-primary" />}
                  <span className="text-xs text-zinc-400 font-body">
                    {role === "creator" ? t("auth.creator") : t("auth.fan")}
                  </span>
                </div>
              </div>

              <form onSubmit={handleRegister} className="space-y-4 p-6 rounded-2xl border border-border bg-card">
                <div className="space-y-1">
                  <label className="text-xs text-zinc-400 font-body font-semibold">
                    {role === "consumer" ? t("auth.name") : t("auth.name")}
                  </label>
                  <Input data-testid="register-name"
                    placeholder={role === "consumer" ? t("auth.namePlaceholderFan") : t("auth.namePlaceholderCreator")}
                    value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))}
                    className="bg-background border-border text-white h-11" required />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-zinc-400 font-body font-semibold">{t("auth.email")}</label>
                  <Input data-testid="register-email" type="email" placeholder="tu@email.com"
                    value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))}
                    className="bg-background border-border text-white h-11" required />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-zinc-400 font-body font-semibold">{t("auth.password")}</label>
                  <div className="relative">
                    <Input data-testid="register-password" type={showPw ? "text" : "password"}
                      placeholder={t("auth.passwordPlaceholder")}
                      value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))}
                      className="bg-background border-border text-white h-11 pr-10" required minLength={6} />
                    <button type="button" onClick={() => setShowPw(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {role === "creator" && (
                  <div className="space-y-3 p-4 rounded-xl"
                    style={{ background: "rgba(212,175,55,0.06)", border: "1px solid rgba(212,175,55,0.25)" }}>
                    {/* Fan referral */}
                    <div>
                      <p className="text-xs font-heading font-bold uppercase tracking-widest"
                        style={{ color: "rgba(212,175,55,0.85)" }}>
                        {t("auth.inviterTitle")}
                      </p>
                      <p className="text-[11px] text-zinc-500 font-body mt-1 leading-relaxed">
                        {t("auth.inviterDescription")}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] text-zinc-400 font-heading uppercase tracking-wider">
                        {t("auth.inviterLabel")}
                      </label>
                      <Input
                        data-testid="register-fan-name"
                        placeholder={t("auth.inviterPlaceholder")}
                        value={fanName}
                        onChange={e => setFanName(e.target.value)}
                        className="bg-background border-zinc-700 text-white h-10 text-sm"
                        style={{ borderColor: fanName ? "rgba(212,175,55,0.5)" : undefined }}
                      />
                    </div>
                    {/* Invite / promo code */}
                    <div className="border-t border-white/5 pt-3 space-y-1">
                      <label className="text-[11px] text-zinc-400 font-heading uppercase tracking-wider">
                        Código promoción <span className="normal-case text-zinc-600">(opcional)</span>
                      </label>
                      <Input
                        data-testid="register-invite-code"
                        placeholder="Código promoción"
                        value={inviteCode}
                        onChange={e => setInviteCode(e.target.value.toUpperCase())}
                        className="bg-background border-zinc-700 text-white h-10 text-sm font-mono"
                        maxLength={100}
                        style={{ borderColor: inviteCode ? "rgba(212,175,55,0.5)" : undefined }}
                      />
                      <p className="text-[10px] text-zinc-600 font-body">
                        Si tienes un código de promoción, introdúcelo aquí.
                      </p>
                    </div>
                  </div>
                )}

                {role === "consumer" && (
                  <div className="p-3 rounded-xl border border-primary/20 bg-primary/5">
                    <p className="text-xs text-zinc-400 font-body leading-relaxed">
                      {t("auth.consumerPrivacyNote")}
                    </p>
                  </div>
                )}

                <Button data-testid="register-submit" type="submit" disabled={loading}
                  className="w-full h-12 bg-neon-green text-black font-heading font-bold uppercase text-sm hover:bg-neon-green/90 disabled:opacity-50"
                  style={{ boxShadow: "4px 4px 0px 0px rgba(204,255,0,0.3)" }}>
                  {loading ? "..." : t("auth.submitRegister")}
                </Button>

                <p className="text-center text-xs text-zinc-600 font-body">
                  {t("auth.alreadyHaveAccount")}{" "}
                  <button type="button" onClick={() => navigate("/login")} className="text-primary hover:underline">{t("auth.login")}</button>
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
